// src/utils/dataTransformers.js

/**
 * Transform stock profile, metrics, ratios, and DCF data to match the app's expected format
 * @param {Object[]} stockProfile - Company profile data
 * @param {Object[]} metrics - Key metrics data
 * @param {Object[]} ratios - Financial ratios data
 * @param {Object[]} dcf - DCF valuation data
 * @param {Object[]} metricsTTM - TTM metrics data (optional)
 * @param {Object[]} ratiosTTM - TTM ratios data (optional)
 * @returns {Object|null} Transformed stock data
 */
export const transformStockData = (
  stockProfile,
  metrics,
  ratios,
  dcf,
  metricsTTM,
  ratiosTTM
) => {
  if (!stockProfile || !stockProfile[0]) return null;

  const profile = stockProfile[0];
  const metric = metrics && metrics.length > 0 ? metrics[0] : {};
  const ratio = ratios && ratios.length > 0 ? ratios[0] : {};
  const dcfData = dcf && dcf.length > 0 ? dcf[0] : {};
  const metricTTM = metricsTTM && metricsTTM[0] ? metricsTTM[0] : {};
  const ratioTTM = ratiosTTM && ratiosTTM[0] ? ratiosTTM[0] : {};

  // Calculate reasonable DCF value if not available
  let dcfValue = profile.price;
  if (dcfData && dcfData.dcf) {
    dcfValue = dcfData.dcf;
  } else if (metric && ratio) {
    // Very simplified DCF fallback
    const growth = ratio.epsgrowth || 0.05;
    const margin = ratio.operatingProfitMargin || 0.15;
    dcfValue = profile.price * (1 + growth * margin * 10);
  }

  // Calculate comparable value (simplified approach)
  const comparableValue = calculateComparableValue(profile, ratio, ratioTTM);

  // Process financial metrics for better display
  const roic = processMetric(metric.roic, metricTTM.roicTTM, 100);
  const operatingMargin = processMetric(
    ratio.operatingProfitMargin,
    ratioTTM.operatingProfitMarginTTM,
    100
  );
  const growthRate = processMetric(ratio.epsgrowth, null, 100);

  // Calculate economic moat score
  const economicMoat = calculateMoatScore(profile, ratio, metric);

  // Calculate earnings stability score
  const earningsStability = calculateStabilityScore(metrics, ratio);

  // Calculate appropriate discount rate based on company characteristics
  const discountRate = calculateDiscountRate(profile, ratio, metric);

  return {
    symbol: profile.symbol,
    name: profile.companyName || profile.name,
    sector: profile.sector || "Unknown",
    currentPrice: profile.price || 0,
    intrinsicValue: dcfValue * 0.7 + comparableValue * 0.3, // Weighted average
    marketCap: profile.mktCap || 0,
    peRatio: profile.pe || 0,
    roic: roic,
    operatingMargin: operatingMargin,
    growthRate: growthRate,
    dividendYield: profile.lastDiv
      ? (profile.lastDiv / profile.price) * 100
      : 0,
    beta: profile.beta || 1,
    debtToEquity: ratio.debtToEquity || metricTTM.debtToEquityTTM || 0,
    economicMoat: economicMoat,
    earningsStability: earningsStability,
    discountRate: discountRate,
    dcfValue: dcfValue,
    comparableValue: comparableValue,
    peerPE:
      profile.sector === "Technology"
        ? 25
        : profile.sector === "Healthcare"
        ? 22
        : 18, // Simplified
    peerEVEBITDA:
      profile.sector === "Technology"
        ? 15
        : profile.sector === "Healthcare"
        ? 14
        : 12, // Simplified
    peerAdjustment: 0, // Would come from peer comparison
    sectorROIC:
      profile.sector === "Technology"
        ? 22.5
        : profile.sector === "Healthcare"
        ? 18.5
        : 15, // Simplified
    sectorMargin:
      profile.sector === "Technology"
        ? 32
        : profile.sector === "Healthcare"
        ? 28
        : 22, // Simplified
    sectorGrowth:
      profile.sector === "Technology"
        ? 10
        : profile.sector === "Healthcare"
        ? 8
        : 5, // Simplified
    priceChange: profile.changes ? (profile.changes / profile.price) * 100 : 0,

    // Add placeholder data for other required fields
    investmentThesis: generateInvestmentThesis(profile, ratio, metric),
    strengths: generateStrengths(profile, ratio, metric),
    weaknesses: generateWeaknesses(profile, ratio, metric),
    recommendation: generateRecommendation(
      profile,
      ratio,
      dcfValue,
      comparableValue
    ),
  };
};

/**
 * Process a metric, handling TTM values and scaling appropriately
 * @param {number} value - Annual metric value
 * @param {number} ttmValue - TTM metric value
 * @param {number} scale - Scale factor (e.g., 100 to convert 0.15 to 15%)
 * @returns {number} Processed value
 */
const processMetric = (value, ttmValue, scale = 1) => {
  // Prefer TTM (more recent) if available
  const baseValue =
    ttmValue !== undefined && ttmValue !== null ? ttmValue : value;

  // If we still don't have a value, provide a reasonable default
  if (baseValue === undefined || baseValue === null) {
    return scale === 100 ? 10 : 0.1; // Default 10% as percent, 0.1 as decimal
  }

  return baseValue * scale;
};

/**
 * Calculate economic moat score (1-5 scale)
 * Evaluates competitive advantages based on financial metrics
 */
const calculateMoatScore = (profile, ratio, metric) => {
  let score = 3; // Default medium moat

  // Profitability metrics
  if ((ratio.operatingProfitMargin || 0) > 0.25) score += 0.5;
  if ((ratio.netProfitMargin || 0) > 0.2) score += 0.5;
  if ((metric.roic || 0) > 0.15) score += 0.5;

  // Financial strength
  if ((ratio.debtToEquity || 1) < 0.5) score += 0.25;
  if ((ratio.interestCoverageRatio || 0) > 10) score += 0.25;

  // Stability and growth
  if ((ratio.returnOnEquity || 0) > 0.2) score += 0.25;
  if ((ratio.epsgrowth || 0) > 0.1) score += 0.25;

  // Dividend history (if applicable)
  if (profile.lastDiv && profile.lastDiv > 0) {
    score += 0.25;
  }

  // Market position
  if (profile.mktCap > 100000000000) {
    // Large cap
    score += 0.25;
  }

  // Cap the score between 1 and 5
  return Math.max(1, Math.min(5, Math.round(score)));
};

/**
 * Calculate earnings stability score (0-1 scale)
 * Higher score means more stable and predictable earnings
 */
const calculateStabilityScore = (metrics, ratio) => {
  // Ideally, this would analyze multiple years of earnings data
  // This is a simplified version based on available metrics

  let score = 0.7; // Default medium-high stability

  // If we have multiple years of metrics, we could calculate variance
  // For now, use some proxy metrics

  // Dividend payers tend to be stable
  if (ratio.dividendYield && ratio.dividendYield > 0) {
    score += 0.1;
  }

  // Higher margins usually mean more stability
  if (ratio.operatingProfitMargin > 0.2) {
    score += 0.05;
  }

  // Low debt usually means more stability
  if (ratio.debtToEquity < 0.5) {
    score += 0.05;
  }

  // Interest coverage ratio indicates stability
  if (ratio.interestCoverageRatio > 8) {
    score += 0.05;
  }

  // Cap at 0.95 as nothing is perfectly stable
  return Math.min(0.95, score);
};

/**
 * Calculate an appropriate discount rate for the company
 * Based on risk factors and company characteristics
 */
const calculateDiscountRate = (profile, ratio, metric) => {
  // Base rate is risk-free rate + equity risk premium
  let rate = 4.5 + 5.0; // E.g., 4.5% risk-free rate + 5% ERP

  // Adjust for company size
  if (profile.mktCap < 2000000000) {
    // Small cap
    rate += 1.5;
  } else if (profile.mktCap < 10000000000) {
    // Mid cap
    rate += 0.5;
  }

  // Adjust for beta (volatility)
  if (profile.beta > 1.5) {
    rate += 1.0;
  } else if (profile.beta > 1.0) {
    rate += 0.5;
  }

  // Adjust for financial risk
  if (ratio.debtToEquity > 1.0) {
    rate += 1.0;
  } else if (ratio.debtToEquity > 0.5) {
    rate += 0.5;
  }

  // Discount for high quality companies
  if (metric.roic > 0.15 && ratio.operatingProfitMargin > 0.2) {
    rate -= 0.5;
  }

  // Cap at reasonable bounds
  return Math.max(7, Math.min(15, rate));
};

/**
 * Calculate comparable company valuation
 */
const calculateComparableValue = (profile, ratio, ratioTTM) => {
  // This would ideally use peer comparison data
  // For simplicity, using sector averages

  let comparableValue = profile.price;

  // Apply sector average P/E
  const sectorPE = getSectorPE(profile.sector);
  if (profile.eps && profile.eps > 0) {
    const peBasedValue = profile.eps * sectorPE;
    comparableValue = peBasedValue;
  }

  return comparableValue;
};

/**
 * Generate investment thesis based on company data
 */
const generateInvestmentThesis = (profile, ratio, metric) => {
  const sector = profile.sector || "this sector";
  const companyName = profile.companyName || profile.name || "This company";

  let strengths = [];

  if ((metric.roic || 0) > 0.15) {
    strengths.push("strong return on invested capital");
  }

  if ((ratio.operatingProfitMargin || 0) > 0.2) {
    strengths.push("high operating margins");
  }

  if ((ratio.debtToEquity || 1) < 0.5) {
    strengths.push("solid balance sheet");
  }

  if ((ratio.epsgrowth || 0) > 0.1) {
    strengths.push("consistent earnings growth");
  }

  if (profile.beta && profile.beta < 1) {
    strengths.push("below-average market volatility");
  }

  if (profile.lastDiv && profile.lastDiv > 0) {
    strengths.push("consistent dividend payments");
  }

  const strengthsText =
    strengths.length > 0
      ? `${companyName} demonstrates ${strengths.join(
          ", "
        )}, which positions it well in ${sector}.`
      : `${companyName} operates in ${sector} with potential for value creation.`;

  return `${strengthsText} Given the current market conditions and the company's financial profile, there may be an opportunity for long-term investors to benefit from potential appreciation toward intrinsic value estimates.`;
};

/**
 * Generate company strengths based on metrics
 */
const generateStrengths = (profile, ratio, metric) => {
  const strengths = [];

  if ((metric.roic || 0) > 0.15) {
    strengths.push("Strong return on invested capital");
  }

  if ((ratio.operatingProfitMargin || 0) > 0.2) {
    strengths.push("Above-average operating margins");
  }

  if ((ratio.debtToEquity || 1) < 0.5) {
    strengths.push("Low debt-to-equity ratio");
  }

  if ((ratio.epsgrowth || 0) > 0.1) {
    strengths.push("Solid earnings growth");
  }

  if (profile.beta && profile.beta < 1) {
    strengths.push("Lower volatility than the market");
  }

  if (profile.lastDiv && profile.lastDiv > 0) {
    strengths.push("Dividend payment history");
  }

  if (profile.mktCap > 10000000000) {
    strengths.push("Strong market position");
  }

  if ((ratio.interestCoverageRatio || 0) > 8) {
    strengths.push("Excellent interest coverage ratio");
  }

  // Ensure we have at least 3 strengths
  if (strengths.length < 3) {
    strengths.push("Established brand in its sector");
    strengths.push("Experienced management team");
    strengths.push("Diversified revenue streams");
  }

  return strengths.slice(0, 5); // Limit to 5 strengths
};

/**
 * Generate company weaknesses based on metrics
 */
const generateWeaknesses = (profile, ratio, metric) => {
  const weaknesses = [];

  if ((metric.roic || 0) < 0.1) {
    weaknesses.push("Below-average return on invested capital");
  }

  if ((ratio.operatingProfitMargin || 0) < 0.15) {
    weaknesses.push("Lower operating margins than peers");
  }

  if ((ratio.debtToEquity || 0) > 1) {
    weaknesses.push("High debt-to-equity ratio");
  }

  if ((ratio.epsgrowth || 0) < 0.05) {
    weaknesses.push("Slower earnings growth");
  }

  if (profile.beta && profile.beta > 1.5) {
    weaknesses.push("Higher volatility than the market");
  }

  if (profile.pe && profile.pe > getSectorPE(profile.sector) * 1.5) {
    weaknesses.push("Above-average valuation metrics");
  }

  if (profile.mktCap < 2000000000) {
    weaknesses.push("Smaller market capitalization");
  }

  if ((ratio.interestCoverageRatio || 10) < 3) {
    weaknesses.push("Lower interest coverage ratio");
  }

  // Ensure we have at least 3 weaknesses
  if (weaknesses.length < 3) {
    weaknesses.push("Operates in a competitive landscape");
    weaknesses.push("Potential regulatory challenges");
    weaknesses.push("Exposure to economic cycles");
  }

  return weaknesses.slice(0, 4); // Limit to 4 weaknesses
};

/**
 * Generate investment recommendation based on valuation
 */
const generateRecommendation = (profile, ratio, dcfValue, comparableValue) => {
  const currentPrice = profile.price || 0;
  const avgIntrinsicValue = (dcfValue + comparableValue) / 2;

  const discount =
    ((avgIntrinsicValue - currentPrice) / avgIntrinsicValue) * 100;

  if (discount > 40) {
    return "Strong Buy";
  } else if (discount > 25) {
    return "Buy";
  } else if (discount > 15) {
    return "Buy on dips";
  } else if (discount > 5) {
    return "Accumulate on weakness";
  } else if (discount > -10) {
    return "Hold";
  } else {
    return "Consider reducing exposure";
  }
};

/**
 * Get average P/E ratio for a sector
 */
const getSectorPE = (sector) => {
  const sectorPEs = {
    Technology: 25,
    Healthcare: 22,
    "Consumer Cyclical": 20,
    "Consumer Defensive": 21,
    "Financial Services": 14,
    Industrials: 18,
    Energy: 15,
    "Basic Materials": 16,
    "Communication Services": 19,
    Utilities: 17,
    "Real Estate": 18,
  };

  return sectorPEs[sector] || 18; // Default to 18 if sector not found
};

/**
 * Transform sector performance data
 */
export const transformSectorData = (sectorData) => {
  if (!sectorData || !Array.isArray(sectorData)) {
    return [];
  }

  // Color palette for sectors
  const sectorColors = {
    Technology: "#8884d8",
    Healthcare: "#82ca9d",
    Financial: "#ffc658",
    "Consumer Cyclical": "#ff8042",
    "Consumer Defensive": "#a4de6c",
    "Communication Services": "#d0ed57",
    Energy: "#83a6ed",
    Industrials: "#8dd1e1",
    Materials: "#a4506c",
    Utilities: "#e6c9a8",
    "Real Estate": "#fdb462",
  };

  return sectorData.map((sector) => {
    // Parse the percentage change value
    const performance = parseFloat(sector.changesPercentage || "0");

    return {
      name: sector.name,
      performance: performance,
      valueOpportunities: 0, // Will be updated as stocks are processed
      color: sectorColors[sector.name] || getRandomColor(),
    };
  });
};

/**
 * Generate random color for sectors without predefined colors
 */
const getRandomColor = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

/**
 * Transform historical price data
 */
export const transformHistoricalPrices = (histData, dcfData) => {
  if (
    !histData ||
    !histData.historical ||
    !Array.isArray(histData.historical)
  ) {
    return [];
  }

  // Get the most recent DCF value
  const currentDCF = dcfData && dcfData.length > 0 ? dcfData[0].dcf : null;

  // For each historical price, create an estimated intrinsic value
  // This is simplified - in reality, intrinsic value doesn't change daily
  return histData.historical
    .slice(-180) // Last 6 months
    .filter((_, index) => index % 7 === 0) // Sample weekly to reduce data points
    .map((day) => {
      // Format date to YYYY-MM
      const date = new Date(day.date);
      const formattedDate = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}`;

      // Create an evolving intrinsic value (purely illustrative)
      // In reality, you'd want to use historical DCF values if available
      const priceToCurrentRatio = day.close / histData.historical[0].close;
      const estimatedIntrinsicValue = currentDCF
        ? currentDCF * (0.9 + 0.1 * priceToCurrentRatio) // Slight variation based on price
        : day.close * 1.2; // Fallback if no DCF

      return {
        symbol: histData.symbol,
        date: formattedDate,
        price: day.close,
        intrinsicValue: estimatedIntrinsicValue,
      };
    })
    .reverse(); // Reverse to get chronological order
};

/**
 * Transform insider transaction data
 */
export const transformInsiderTransactions = (transactions) => {
  if (!transactions || !Array.isArray(transactions)) {
    return [];
  }

  return transactions.slice(0, 10).map((transaction) => {
    return {
      symbol: transaction.symbol,
      date: transaction.transactionDate,
      insider: transaction.reportingName,
      title: transaction.typeOfOwner,
      type: transaction.acquistionOrDisposition === "A" ? "Buy" : "Sell",
      shares: Math.abs(transaction.securitiesTransacted),
      price: transaction.price,
    };
  });
};

/**
 * Transform financial metrics data
 */
export const transformFinancialMetrics = (metrics, ratios) => {
  if (!metrics || !Array.isArray(metrics) || metrics.length === 0) {
    return [];
  }

  // Get corresponding ratios for the same years
  const ratiosByDate = {};
  if (ratios && Array.isArray(ratios)) {
    ratios.forEach((ratio) => {
      ratiosByDate[ratio.date] = ratio;
    });
  }

  return metrics.map((metric) => {
    const ratio = ratiosByDate[metric.date] || {};

    return {
      symbol: metric.symbol,
      year: new Date(metric.date).getFullYear().toString(),
      revenue: metric.revenue / 1000000000, // Convert to billions
      operatingMargin: (ratio.operatingProfitMargin || 0) * 100,
      roic: (metric.roic || 0) * 100,
    };
  });
};
